import http from '@/plugins/api';
import * as types from './types';
import { useSendbird } from '@/plugins/sendbird/sendbird-client';
import {
  isClever,
  getEmail,
  isSendbirdAuditingEnabled
} from '@/plugins/globals.js'
import { startLoading } from '@/helpers/loading';
import { getI18nInstance } from '@/plugins/i18n';
import { normalizeServiceHistory } from './normalize';

const { getMessageList } = useSendbird();

/** Normalize language (e.g., 'es-ES' -> 'es', 'gr' -> 'el') */
const normalizeLang = (l) => {
  if (!l) return 'en';
  const lc = l.toLowerCase();
  if (lc === 'gr') return 'el';
  return lc.split('-')[0];
};

/** Get the current UI language from i18n (falls back to 'en') */
const getUiLang = () => {
  try {
    const i18n = getI18nInstance?.();
    const v = i18n?.global?.locale?.value ?? i18n?.global?.locale;
    return normalizeLang(v || 'en');
  } catch {
    return 'en';
  }
};

function mapUserMessage(msg, uiLang, userProfile) {
  const userId = msg.sender?.userId || '';
  const isSentByCurrentUser = userId === userProfile.email;
  const name = isSentByCurrentUser ? 'me' : (msg.sender?.nickname || '');

  const translations = msg.translations || {};
  const base = normalizeLang(uiLang);

  // Prefer UI language, then its base (e.g., 'es-ES' -> 'es'), then legacy 'gr' => 'el', then keep current message.
  const translated =
    translations[uiLang] ??
    translations[base] ??
    // (base === 'el' ? translations.gr : undefined) ??
    msg.message;

  return {
    name,
    type: isSentByCurrentUser ? 'sent' : 'received',
    messageType: 'user',
    contentType: 'text/plain',
    plainUrl: '',
    createdAt: msg.createdAt || Date.now(),
    email: userId,
    messageId: msg.messageId || msg.reqId || Math.random().toString(36).substring(2),
    reqId: msg.reqId || '',
    message: translated,
  };
}

function mapFileMessage(msg, userProfile) {
  const userId = msg.sender?.userId || '';
  const isSentByCurrentUser = userId === userProfile.email;
  const name = isSentByCurrentUser ? 'me' : (msg.sender?.nickname || '');

  return {
    name,
    type: isSentByCurrentUser ? 'sent' : 'received',
    messageType: 'file',
    contentType: msg.type || '',
    plainUrl: msg.url || msg.plainUrl || '',
    createdAt: msg.createdAt || Date.now(),
    email: userId,
    messageId: msg.messageId || msg.reqId || Math.random().toString(36).substring(2),
    reqId: msg.reqId || '',
  };
}

export default {
  resetMessageList({ commit }, channelUrl) {
    console.log('DEBUG => [ACTION => resetMessageList]', channelUrl);
    commit(types.RESET_MESSAGES, channelUrl);
  },

  async loadFullChatHistory({ commit, rootGetters }, { channelUrl, channelSet }) {
    console.log('DEBUG => [ACTION => loadFullChatHistory]', channelUrl);
  },

  async loadRecentChatMessages({ commit }, { channelUrl, channelSet }) {
    const userLang = getUiLang();
    const baseLang = normalizeLang(userLang);
    const languages = [userLang, 'en'].filter(Boolean);

    const channel = channelSet?.channel;
    if (!channel) return console.warn(`[loadRecentChatMessages] Missing channel for ${channelUrl}`);

    const messages = await getMessageList(channelSet);
    const safeList = messages.filter(m => m && m.sender);

    for (const m of safeList) {
      if (m.messageType === 'user') {
        try {
          const translated = await channel.translateUserMessage(m, languages);
          m.message = isClever()
            ? translated.message
            : (
                translated.translations?.[userLang] ??
                translated.translations?.[baseLang] ??
                translated.message
              );
        } catch {
          // swallow translation issues
        }
      }
    }

    safeList.sort((a, b) => a.createdAt - b.createdAt);
    commit(types.SET_RECENT_MESSAGES, { channelUrl, messages: safeList });
  },

  addLiveMessage({ commit, rootGetters }, { channelUrl, message }) {
    const uiLang = getUiLang();
    const userProfile = rootGetters['auth/userProfile'];

    const isFileMessage = message.messageType === 'file';

    const mappedMessage = isFileMessage
      ? mapFileMessage(message, userProfile)
      : mapUserMessage(message, uiLang, userProfile);

    const mutationType = isFileMessage ? types.ADD_ATTACHMENT_MESSAGE : types.ADD_MESSAGE;

    commit(mutationType, {
      channelUrl,
      message: mappedMessage,
      language: uiLang,
      userProfile,
      hasmore: false,
    });
  },

  addMessageToList({ commit, rootGetters }, { channelUrl, message, hasmore }) {
    // Preserve existing behavior but align language to UI language for consistency
    const uiLang = getUiLang();
    const userProfile = rootGetters['auth/userProfile'];

    commit(types.ADD_MESSAGE, {
      channelUrl,
      message,
      language: uiLang,
      userProfile,
      hasmore,
    });
  },

  addAttachmentToMessageList({ commit, rootGetters }, { channelUrl, message, hasmore }) {
    const userProfile = rootGetters['auth/userProfile'];

    commit(types.ADD_ATTACHMENT_MESSAGE, {
      channelUrl,
      message,
      userProfile,
      hasmore,
    });
  },

  setTyping({ commit, dispatch }, channel) {
    commit(types.SET_TYPING, channel);
    if (channel) {
      setTimeout(() => {
        commit(types.SET_TYPING, null);
      }, 2500);
    }
  },

  resetChatMessages({ commit }, channelUrl) {
    commit(types.RESET_CHAT_MESSAGES, channelUrl);
  },

  async loadChatHistory({ commit, dispatch, rootGetters }, { channelUrl, id }) {
    const r = await startLoading(dispatch, 'sendbird/loadChatHistory', () =>
      http.get(`Message/ServiceRequestMessages?id=${id}&language=en`)
    );

    const language = rootGetters['auth/language'] || 'en';
    const userProfile = rootGetters['auth/userProfile'] || {};
    const normalized = normalizeServiceHistory(r.data, language, userProfile);

    // Store as HISTORY for this channel
    commit(types.SET_CHAT_MESSAGES, {
      channelUrl,
      messages: normalized,
      mode: 'history', // tell the mutation this is server history
    });

    return normalized;
  },
  async getMessageCounts({ commit, dispatch }, data) {
    const email = data.email ?? await getEmail() ?? '';
    const workOrderId = data.workOrderId ?? '';

    const r = await startLoading(dispatch, 'sendbird/getMessageCounts', () =>
      http.get(`Message/DirectUserMessageCount/?username=${email}&workOrderId=${workOrderId}&operatorname=`)
    );

    commit(types.SET_MESSAGECOUNTS, r.data);
  },

  resetChannels({ commit }) {
    commit(types.RESET_CHANNELS);
  },

  async getUserChannels({ commit, dispatch }, options) {
    const r = await startLoading(dispatch, 'sendbird/getUserChannels', () =>
      http.get(
        `chat/SendBirdUserChannels?userid=${encodeURIComponent(options.userid)}&token=${options.token}&pagesize=${options.pagesize}&search=${encodeURIComponent(options.search)}`
      )
    );
    const payload = r?.data ?? {};

    // Normalize different shapes safely:
    // - { channels: [...], next: '...' }
    // - plain array: [...]
    // - { items: [...], token: '...' } (just in case)
    const channels = Array.isArray(payload.channels) ? payload.channels : Array.isArray(payload) ? payload : Array.isArray(payload.items) ? payload.items : [];

    const next =
      payload.next ?? payload.token ?? '';

    commit(types.SET_CHANNELS, { channels, next });
  },

  async syncApps({ dispatch }, message) {
    const r = await startLoading(dispatch, 'sendbird/syncApps', () =>
      http.post('Message/SyncApps', message)
    );
    return r.data;
  },

  async audit({ dispatch }, message) {
    if (await isSendbirdAuditingEnabled()) {
      const r = await startLoading(dispatch, 'sendbird/audit', () =>
        http.post('Sendbird/Audit', message)
      );
      return r.data;
    }
  },
  resetChat({ commit }, channelUrl) {
    commit(types.RESET_CHAT, channelUrl);
  },
};
